/**
 * What entities can sense, and what the player can see.
 *
 * Everything here returns a 0..1 signal rather than a boolean, because
 * awareness in this game is a slope: a shape half-glimpsed at the end of a dark
 * hall should raise an entity's confidence slowly, and a sprinting player under
 * a working light should raise it immediately.
 */

import { hasLineOfSight } from '../core/grid';
import { TUNING } from '../core/config';
import { clamp01, DEG } from '../core/math';
import type { EntityDef, EntityState, Grid, PlayerState, Vec2 } from '../core/types';

/**
 * Half-angle of the player's effective view cone. The camera runs at a 75°
 * vertical FOV, which is a good deal wider horizontally; 55° is generous
 * without letting things count as "seen" out at the very edge of the screen.
 */
export const PLAYER_HALF_FOV = 55 * DEG;

/** Cosine of the angle from `yaw`'s forward to the direction of `to`. */
export function facingCos(from: Vec2, yaw: number, to: Vec2): number {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const l = Math.hypot(dx, dz);
  if (l < 1e-4) return 1;
  // yaw = 0 looks down -Z.
  return (dx * Math.sin(yaw) + dz * -Math.cos(yaw)) / l;
}

/**
 * Geometric visibility: is `pos` inside the player's view cone with nothing
 * solid in the way? Deliberately ignores lighting — this is what the renderer
 * draws, and `EntityState.visible` promises line of sight, not legibility.
 */
export function playerCanSee(grid: Grid, player: PlayerState, pos: Vec2, maxDist: number): boolean {
  const dx = pos.x - player.pos.x;
  const dz = pos.z - player.pos.z;
  const d2 = dx * dx + dz * dz;
  if (d2 > maxDist * maxDist) return false;
  if (d2 < 1.2) return true; // practically on top of the player
  if (facingCos(player.pos, player.yaw, pos) < Math.cos(PLAYER_HALF_FOV)) return false;
  return hasLineOfSight(grid, player.pos, pos);
}

/** True when the player is looking almost exactly at `pos`, not merely past it. */
export function playerStaringAt(grid: Grid, player: PlayerState, pos: Vec2, cos: number): boolean {
  if (facingCos(player.pos, player.yaw, pos) < cos) return false;
  return hasLineOfSight(grid, player.pos, pos);
}

/**
 * How strongly an entity can see the player right now, 0..1.
 *
 * Light does most of the work: a pitch-dark room cuts sight range to a quarter,
 * and an on flashlight all but hands the player over. `hidden` (crouched and
 * still) shrinks the range again. Entities with `sightRange` 0 are blind and
 * always return 0 — the hound never gets a sight signal at all.
 */
export function sightSignal(
  def: EntityDef,
  state: EntityState,
  grid: Grid,
  player: PlayerState,
  playerLight: number,
): number {
  if (def.sightRange <= 0 || def.fov <= 0) return 0;
  const d = state.distToPlayer;
  if (d > def.sightRange) return 0;

  // A lit torch is a beacon regardless of the room's own lighting.
  const light = Math.max(playerLight, player.flashlightOn ? 0.9 : 0);
  let range = def.sightRange * (0.22 + 0.78 * light);
  if (player.hidden) range *= 0.45;
  if (d > range) return 0;

  if (facingCos(state.pos, state.yaw, player.pos) < Math.cos(def.fov * 0.5)) return 0;
  if (!hasLineOfSight(grid, state.pos, player.pos)) return 0;

  return clamp01(1 - d / range) * (0.35 + 0.65 * light);
}

/**
 * How strongly an entity can hear the player right now, 0..1. Walls muffle but
 * do not stop sound, which is the whole point of the hound.
 */
export function hearSignal(def: EntityDef, state: EntityState, grid: Grid, player: PlayerState): number {
  if (def.hearingRange <= 0) return 0;
  let noise = player.noise;
  if (player.hidden) noise *= 0.3;
  if (noise <= TUNING.entities.noiseThreshold * 0.5) return 0;

  const range = def.hearingRange * (0.35 + 0.65 * noise);
  const d = state.distToPlayer;
  if (d > range) return 0;

  let s = clamp01(1 - d / range) * noise;
  if (!hasLineOfSight(grid, state.pos, player.pos)) s *= 0.6;
  return s;
}

/**
 * Loudness of a one-off noise as heard from `pos`. Used by `hearNoise` so a
 * single sprint footfall or a splash can hand an entity a position without any
 * continuous tracking.
 */
export function noiseAt(def: EntityDef, grid: Grid, listener: Vec2, source: Vec2, loudness: number): number {
  if (def.hearingRange <= 0) return 0;
  const dx = source.x - listener.x;
  const dz = source.z - listener.z;
  const d = Math.hypot(dx, dz);
  const range = def.hearingRange * (0.4 + 0.6 * loudness);
  if (d > range) return 0;
  let s = clamp01(1 - d / range) * loudness;
  if (!hasLineOfSight(grid, listener, source)) s *= 0.6;
  return s;
}
